/* Admin portal , Magic Cruises (hidden route, not linked anywhere).
   Login → tabbed dashboard:
   - Availability  (block/unblock dates on the public calendar)
   - Ticket Events (edit Adult/Child prices for every Stripe-driven ticketed cruise)
   - Packages     (edit cruise package tiers, inclusions, menus line-by-line)
   - Enquiries    (read-only leads inbox)
   - Sales        (paid Stripe orders)
   - Password     (change admin password)
*/
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, ShieldCheck, Calendar, Ticket, Package, Inbox, ShoppingBag, KeyRound, History, Menu, X, LayoutGrid, Anchor } from "lucide-react";
import { adminApi, getToken, setToken, clearToken } from "./admin/adminApi";
import AdminAvailability from "./admin/AdminAvailability";
import AdminTicketEvents from "./admin/AdminTicketEvents";
import AdminPackages from "./admin/AdminPackages";
import AdminEnquiries from "./admin/AdminEnquiries";
import AdminOrders from "./admin/AdminOrders";
import AdminPassword from "./admin/AdminPassword";
import AdminAuditLog from "./admin/AdminAuditLog";
import AdminHomeOrder from "./admin/AdminHomeOrder";
import AdminFeaturedTicket from "./admin/AdminFeaturedTicket";
import AdminHoldingDeposits from "./admin/AdminHoldingDeposits";

const TABS = [
 { id: "availability", label: "Availability", Icon: Calendar, Component: AdminAvailability },
 { id: "ticket-events", label: "Ticket Events", Icon: Ticket, Component: AdminTicketEvents },
 { id: "packages", label: "Packages & Menus", Icon: Package, Component: AdminPackages },
 { id: "home-order", label: "Home Order", Icon: LayoutGrid, Component: AdminHomeOrder },
 { id: "featured-ticket", label: "Featured Ticket", Icon: Ticket, Component: AdminFeaturedTicket },
 { id: "enquiries", label: "Enquiries", Icon: Inbox, Component: AdminEnquiries },
 { id: "orders", label: "Ticket Sales", Icon: ShoppingBag, Component: AdminOrders },
 { id: "deposits", label: "Holding Deposits", Icon: Anchor, Component: AdminHoldingDeposits },
 { id: "audit", label: "Audit Log", Icon: History, Component: AdminAuditLog },
 { id: "password", label: "Change Password", Icon: KeyRound, Component: AdminPassword },
];

/* ─────────────────────── ROOT ─────────────────────── */
export default function AdminPortal() {
 const [token, setLocalToken] = useState(() => getToken());

 // Verify any stored token on mount; clear if invalid/expired
 useEffect(() => {
  if (!token) return;
  adminApi("/admin/me").catch(() => {
   clearToken();
   setLocalToken(null);
  });
 }, [token]);

 // Don't index this page
 useEffect(() => {
  document.title = "Admin · Magic Cruises";
  let robots = document.querySelector('meta[name="robots"]');
  const prev = robots ? robots.getAttribute("content") : null;
  if (!robots) {
   robots = document.createElement("meta");
   robots.setAttribute("name", "robots");
   document.head.appendChild(robots);
  }
  robots.setAttribute("content", "noindex, nofollow, noarchive");
  return () => { if (prev != null) robots.setAttribute("content", prev); };
 }, []);

 const handleLogin = (newToken) => { setToken(newToken); setLocalToken(newToken); };
 const handleLogout = () => { clearToken(); setLocalToken(null); };

 return (
  <div className="min-h-screen bg-[#0A0A12] text-white pt-20 pb-20 px-5 lg:px-10" data-testid="admin-portal">
   {token
    ? <AdminDashboard onLogout={handleLogout} />
    : <AdminLogin onLogin={handleLogin} />}
  </div>
 );
}

/* ─────────────────────── LOGIN ─────────────────────── */
function AdminLogin({ onLogin }) {
 const [password, setPassword] = useState("");
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState("");
 const [showPw, setShowPw] = useState(false);

 const submit = async (e) => {
  e.preventDefault();
  setError("");
  setLoading(true);
  try {
   const data = await adminApi("/admin/login", { method: "POST", body: { password } });
   if (data && data.token) onLogin(data.token);
   else setError("Unexpected response from server.");
  } catch (err) { setError(err.message || "Login failed"); }
  finally { setLoading(false); }
 };

 return (
  <div className="mx-auto max-w-md pt-8" data-testid="admin-login">
   <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-5">
    <ShieldCheck className="w-4 h-4" /> Magic Cruises · Admin
   </div>
   <h1 className="font-black tracking-tight leading-[0.95] text-4xl lg:text-5xl mb-3" data-testid="admin-login-h1">Sign in.</h1>
   <p className="text-white/65 text-sm leading-relaxed mb-8">Restricted access. This page is not indexed and not linked from the public site.</p>

   <form onSubmit={submit} className="space-y-5">
    <label className="block">
     <span className="block text-[10px] uppercase tracking-[0.28em] font-bold text-white/55 mb-2">Admin password</span>
     <div className="relative">
      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
      <input
       type={showPw ? "text" : "password"}
       autoFocus
       autoComplete="current-password"
       value={password}
       onChange={(e) => setPassword(e.target.value)}
       className="w-full bg-white/[0.04] border border-white/15 rounded-2xl pl-11 pr-14 py-4 text-white text-sm placeholder-white/30 focus:outline-none focus:border-amber-400/60 focus:bg-white/[0.06] transition-colors"
       placeholder="Enter the admin password"
       data-testid="admin-login-password"
      />
      <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.18em] font-bold text-white/50 hover:text-white px-2 py-1" data-testid="admin-login-show-pw">
       {showPw ? "Hide" : "Show"}
      </button>
     </div>
    </label>
    {error && <div className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200" data-testid="admin-login-error">{error}</div>}
    <button type="submit" disabled={loading || !password} className="w-full px-6 py-4 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-[#0A0A0A] font-black text-xs tracking-[0.22em] uppercase disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] transition-transform" data-testid="admin-login-submit">
     {loading ? "Signing in…" : "Sign in"}
    </button>
   </form>
  </div>
 );
}

/* ─────────────────────── DASHBOARD ─────────────────────── */
function AdminDashboard({ onLogout }) {
 const navigate = useNavigate();
 const [active, setActive] = useState("availability");
 const [navOpen, setNavOpen] = useState(false);
 const [toast, setToast] = useState(null);

 const showToast = (msg, type = "ok") => {
  setToast({ msg, type });
  setTimeout(() => setToast(null), 3500);
 };

 const tab = TABS.find((t) => t.id === active) || TABS[0];
 const TabComponent = tab.Component;

 return (
  <div className="mx-auto max-w-7xl" data-testid="admin-dashboard">
   {/* Header */}
   <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
    <div>
     <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.32em] font-black mb-3">
      <ShieldCheck className="w-3.5 h-3.5" /> Admin · {tab.label}
     </div>
     <h1 className="font-black tracking-tight leading-[0.95] text-3xl lg:text-4xl">
      Magic Cruises <span className="italic font-light text-amber-300">control room.</span>
     </h1>
    </div>
    <div className="flex items-center gap-2">
     <button onClick={() => navigate("/")} className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:bg-white/[0.06] text-white/75 hover:text-white text-[10px] uppercase tracking-[0.22em] font-bold transition-colors" data-testid="admin-view-public-site">
      View site
     </button>
     <button onClick={onLogout} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:bg-white/[0.06] text-white/75 hover:text-white text-[10px] uppercase tracking-[0.22em] font-bold transition-colors" data-testid="admin-logout">
      <LogOut className="w-3.5 h-3.5" /> Sign out
     </button>
    </div>
   </div>

   {/* Mobile tab toggle */}
   <div className="lg:hidden mb-4">
    <button onClick={() => setNavOpen((v) => !v)} className="w-full inline-flex items-center justify-between gap-2 px-5 py-3 rounded-2xl bg-white/[0.04] border border-white/15 text-white font-bold text-sm" data-testid="admin-mobile-tabs-toggle">
     <span className="inline-flex items-center gap-2"><tab.Icon className="w-4 h-4 text-amber-300" /> {tab.label}</span>
     {navOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
    </button>
   </div>

   <div className="grid lg:grid-cols-[15rem_1fr] gap-6 lg:gap-10">
    {/* Side nav */}
    <nav className={`${navOpen ? "block" : "hidden"} lg:block`} data-testid="admin-tabs-nav">
     <div className="lg:sticky lg:top-24 space-y-1">
      {TABS.map((t) => {
       const Icon = t.Icon;
       const isActive = t.id === active;
       return (
        <button
         key={t.id}
         onClick={() => { setActive(t.id); setNavOpen(false); }}
         className={`w-full inline-flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm font-bold transition-colors ${isActive ? "bg-amber-400 text-[#0A0A0A]" : "text-white/70 hover:bg-white/[0.05] hover:text-white"}`}
         data-testid={`admin-tab-btn-${t.id}`}
        >
         <Icon className="w-4 h-4 shrink-0" /> {t.label}
        </button>
       );
      })}
     </div>
    </nav>

    {/* Active tab content */}
    <div className="min-w-0">
     <TabComponent onUnauthorized={onLogout} showToast={showToast} />
    </div>
   </div>

   {toast && (
    <div
     className={`fixed bottom-6 right-6 px-5 py-3 rounded-2xl shadow-2xl text-sm font-bold z-50 ${toast.type === "err" ? "bg-rose-500 text-white" : "bg-emerald-500 text-white"}`}
     data-testid="admin-toast"
    >
     {toast.msg}
    </div>
   )}
  </div>
 );
}
